import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import actions from '../store/appActions';
import selectors from '../store/appSelectors';
import { evaluateNetworkQuality } from '../utils/networkQualityMonitor';

class AppContainer extends React.Component {
  constructor(props) {
    super(props);
    // Intercept global fetch so auth state is refreshed on any 401 response.
    const fetch = global.fetch;
    global.fetch = function(url, options) {
      return fetch(url, options).then(response => {
        if (response && response.status === 401) {
          props.getAuthenticationStatus();
        }
        return response;
      });
    };

    this.networkLightTimer = null;
    this.networkExpandedTimer = null;
    this.networkToastTimer = null;
  }

  async componentDidMount() {
    await this.props.ping();
    this.startNetworkMonitor();
    if (this.props.pingInfo.ping === 'pong') {
      if (!this.props.currentUser) {
        this.props.getAuthenticationStatus();
      }
    }
  }

  componentWillUnmount() {
    if (this.networkLightTimer) clearInterval(this.networkLightTimer);
    if (this.networkExpandedTimer) clearInterval(this.networkExpandedTimer);
    if (this.networkToastTimer) clearTimeout(this.networkToastTimer);
  }

  async componentDidUpdate(prevProps) {
    if (prevProps.isAuthenticated && !this.props.isAuthenticated) {
      this.props.localLogout();
    } else if (!prevProps.isAuthenticated && this.props.isAuthenticated) {
      this.props.getLanguages();
      this.props.describeMap();
      // Run the expanded passive probe right after login so the header indicator
      // is up-to-date without waiting for the 15 minute interval.
      this.evaluateAndPublishNetworkQuality({
        windowMs: 30 * 60 * 1000,
        includeConnection: true,
        allowToastFromUnknown: false
      });
    }
  }

  render() {
    return this.props.render(this.props);
  }

  startNetworkMonitor = () => {
    if (this.networkLightTimer || this.networkExpandedTimer) return;

    // Light probe: quick internet status check.
    this.networkLightTimer = setInterval(() => {
      this.evaluateAndPublishNetworkQuality({
        windowMs: 5 * 60 * 1000,
        includeConnection: false,
        allowToastFromUnknown: false
      });
    }, 3 * 60 * 1000);

    // Expanded probe: internet status check with connection hints.
    this.networkExpandedTimer = setInterval(() => {
      this.evaluateAndPublishNetworkQuality({
        windowMs: 30 * 60 * 1000,
        includeConnection: true,
        allowToastFromUnknown: false
      });
    }, 15 * 60 * 1000);

    // Initial evaluation shortly after startup.
    setTimeout(() => {
      this.evaluateAndPublishNetworkQuality({
        windowMs: 5 * 60 * 1000,
        includeConnection: true,
        allowToastFromUnknown: false
      });
    }, 15 * 1000);
  };

  evaluateAndPublishNetworkQuality = ({
    windowMs,
    includeConnection,
    allowToastFromUnknown
  }) => {
    const result = evaluateNetworkQuality({ windowMs, includeConnection });
    const prevQuality = this.props.networkQuality;
    const nextQuality = result.quality;

    this.props.updateNetworkQuality(nextQuality, result.stats);

    const shouldToast =
      prevQuality &&
      prevQuality !== nextQuality &&
      (allowToastFromUnknown ? true : prevQuality !== 'unknown');

    if (shouldToast) {
      const label =
        nextQuality === 'offline'
          ? 'offline'
          : nextQuality === 'healthy'
          ? 'fast'
          : nextQuality === 'degraded'
          ? 'moderate'
          : nextQuality === 'poor'
          ? 'slow'
          : 'moderate';
      this.props.showNetworkQualityToast(
        `Network quality: ${label}`,
        nextQuality
      );

      if (this.networkToastTimer) clearTimeout(this.networkToastTimer);
      this.networkToastTimer = setTimeout(() => {
        this.props.clearNetworkQualityToast();
      }, 10 * 1000);
    }
  };
}

AppContainer.propTypes = {
  ping: PropTypes.func.isRequired,
  currentUser: PropTypes.string,
  isAuthenticated: PropTypes.bool,
  getAuthenticationStatus: PropTypes.func.isRequired,
  render: PropTypes.func.isRequired,
  submitLogout: PropTypes.func.isRequired,
  localLogout: PropTypes.func.isRequired
};

const mapStateToProps = (state, ownProps) => {
  return {
    pingInfo: selectors.ping.pingInfo(state.ping),
    isAuthenticated: selectors.user.isCurrentUserAuthenticated(state.user),
    currentUser: selectors.user.currentUser(state.user),
    LKPUpdate: selectors.user.LKPUpdate(state.user),
    languages: selectors.ontology.languages(state.ontology),
    alertsTotal: selectors.timeline.alertsTotal(state.timeline),
    alertSounds: selectors.timeline.alertSounds(state.timeline),
    showAgentNames: selectors.ontology.showAgentNames(state.ontology),
    hideOldAgents: selectors.ontology.hideOldAgents(state.ontology),
    showInactiveAgents: selectors.ontology.showInactiveAgents(state.ontology),
    showObservationClusters: selectors.ontology.showObservationClusters(
      state.ontology
    ),
    observationClusterThreshold: selectors.ontology.observationClusterThreshold(
      state.ontology
    ),
    trackColor: selectors.ontology.trackColor(state.ontology),
    showGrid: selectors.ontology.showGrid(state.ontology),
    showCoordinates: selectors.ontology.showCoordinates(state.ontology),
    networkQuality: selectors.network.quality(state.network),
    networkStats: selectors.network.stats(state.network),
    ...ownProps
  };
};

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      ping: actions.ping.ping,
      submitLogout: actions.user.submitLogout,
      localLogout: actions.user.localLogout,
      describeMap: actions.user.describeMap,
      getAuthenticationStatus: actions.user.getAuthenticationStatus,
      getLanguages: actions.ontology.getLanguages,
      setSelectedLanguage: actions.ontology.setSelectedLanguage,
      setUpdateInterval: actions.ontology.setUpdateInterval,
      setAlertTimeline: actions.ontology.setAlertTimeline,
      setShowAgentNames: actions.ontology.setShowAgentNames,
      setHideOldAgents: actions.ontology.setHideOldAgents,
      setShowInactiveAgents: actions.ontology.setShowInactiveAgents,
      setShowObservationClusters: actions.ontology.setShowObservationClusters,
      setObservationClusterThreshold:
        actions.ontology.setObservationClusterThreshold,
      setTrackColor: actions.ontology.setTrackColor,
      setShowGrid: actions.ontology.setShowGrid,
      setShowCoordinates: actions.ontology.setShowCoordinates,
      allowAlertSounds: actions.timeline.allowAlertSounds,
      disableAlertSounds: actions.timeline.disableAlertSounds,
      updateNetworkQuality: actions.network.updateNetworkQuality,
      showNetworkQualityToast: actions.network.showNetworkQualityToast,
      clearNetworkQualityToast: actions.network.clearNetworkQualityToast
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(AppContainer);
